import React from 'react';
import { Container } from '../components/common/Container';
import { Card } from '../components/common/Card';
import { 
  CalculatorIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  FolderOpenIcon,
  CodeBracketIcon,
  UsersIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/outline';
import { BOOKING_DURATION_MINUTES, BOOKING_URL } from '../config/contact';

const services = [
  {
    Icon: ArrowPathIcon,
    title: 'County Data Automation',
    description: 'Bring assessor values and public-record property data into organized records without repeating the same lookup and re-entry work across the portfolio.'
  },
  {
    Icon: ClipboardDocumentListIcon,
    title: 'Appeal Workflow Management',
    description: 'Track every appeal from notice through decision with clear stages, ownership, next actions, and a complete history for each property.'
  },
  {
    Icon: CalendarDaysIcon,
    title: 'Deadline Control',
    description: 'See upcoming and overdue filings, hearings, and responses in one deadline queue and portfolio-wide calendar.'
  },
  {
    Icon: CalculatorIcon,
    title: 'Cost Approach Valuation',
    description: 'Industry-standard cost approach methodology with automated calculations, current cost lookups, and depreciation schedules. Generate comprehensive cost-based valuations in minutes.'
  },
  {
    Icon: ChartBarIcon,
    title: 'Income Approach Analysis',
    description: 'Powerful income-based valuation tools with cap rate analysis, NOI calculations, and market comparisons. Perfect for commercial properties and income-producing assets.'
  },
  {
    Icon: PresentationChartLineIcon,
    title: 'Equity Approach',
    description: 'Automated comparable property analysis using assessor data. Find and bracket similar properties to build compelling equity arguments.'
  },
  {
    Icon: PresentationChartLineIcon,
    title: 'Reporting & Workups',
    description: 'Turn the data already in the workflow into status reports, exports, and complete valuation packages with supporting evidence.'
  },
  {
    Icon: CodeBracketIcon,
    title: 'API Integration',
    description: 'Enterprise-grade REST API for seamless integration with your existing systems. Programmatic access to property data, appeal tracking, and reporting capabilities.'
  },
  {
    Icon: FolderOpenIcon,
    title: 'Document Management',
    description: 'Centralized document storage with version control. Upload and organize rent rolls, income statements, photos, appraisals, and all supporting documentation.'
  },
  {
    Icon: UsersIcon,
    title: 'Team Collaboration',
    description: 'Role-based access control, team assignments, and client portals. Collaborate efficiently across your organization with granular permission controls.'
  }
];

export const Services: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="bg-brand-blue py-20">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              The operating tools property tax teams use every day
            </h1>
            <p className="text-xl text-brand-blue-100">
              Automate county data, keep every appeal visible, and stay ahead of
              the deadlines that move the work forward.
            </p>
          </div>
        </Container>
      </section>
      
      {/* Services Grid */}
      <section className="py-24 bg-white">
        <Container>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} hover>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-brand-red rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <service.Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-brand-blue mb-3">
                      {service.title}
                    </h3>
                    <p className="text-brand-white-700">
                      {service.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Container>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 bg-brand-blue-50">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-brand-blue mb-6">
              Review your workflow in {BOOKING_DURATION_MINUTES} minutes
            </h2>
            <p className="text-lg text-brand-white-700 mb-8">
              Bring your current process and we’ll show where Appeal Advantage can
              remove manual work and give the team more control.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-brand-red hover:bg-brand-red-600 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Book a {BOOKING_DURATION_MINUTES}-minute workflow review
            </a>
          </div>
        </Container>
      </section>
    </>
  );
};
